/**
 * TerrainAsset.ts — heightfield + classification mask loaded from
 * `public/terrain/<name>/{heightmap.png, terrain_meta.json}`.
 *
 * Baked by the Laravel admin pipeline (App\Services\Terrains\Bake):
 *  - heightmap.png — 8-bit grayscale, normalised over [elevation_min, elevation_max]
 *  - terrain_meta  — bbox, width_m, height_m, elevation min/max/range, water_level_m
 *
 * Body coords convention used by the rest of CastOffPro:
 *   +X east, +Y up, +Z south. Y=0 is mean water level.
 *
 * The loader emits a `TerrainAsset` with:
 *  - heights  : Float32Array, world-space metres above water level (Y axis)
 */

import type { TerrainGameplayRole } from "./terrainCatalog";

export interface ClassificationLayerMeta {
  id: string;
  file: string;
  style?: string;
  map_id?: string;
  role?: string;
  priority?: number;
  opacity?: number;
}

export interface TerrainMetaJson {
  bbox: {
    lon_min: number;
    lat_min: number;
    lon_max: number;
    lat_max: number;
  };
  zoom: number;
  output_size: number;
  width_m: number;
  height_m: number;
  dem_source: string;
  elevation_min_m: number;
  elevation_max_m: number;
  elevation_range_m: number;
  water_level_m: number;
  /** Ordered semantic classification rasters generated from MapTiler styles. */
  classification_layers?: ClassificationLayerMeta[];
  /** Palette / color-rule JSON for `classification_layers`. */
  terrain_layers?: string;
  /** Baked L1/L2 surface raster consumed by runtime directly. */
  compiled_surface?: TerrainCompiledRasterMeta;
  /** Optional baked coverage raster; RGBA channels are feature-specific. */
  compiled_coverage?: TerrainCompiledRasterMeta;
  /** Optional baked marker raster; stores AnnotationMarker enum values. */
  compiled_markers?: TerrainCompiledRasterMeta;
  /** Baked L3 world-object feature file. */
  world_objects?: string;
  /** Baked L4 gameplay-zone feature file. */
  gameplay_zones?: string;
  /** Baked road polylines (centred world frame) used by RoadRenderPass. */
  roads?: string;
  /** Baked railway polylines (centred world frame) used by RailRenderPass. */
  rails?: string;
  /** Max depth in metres used when carving dock / bridge bed over classified water. */
  max_depth_m?: number;
  /** Default height (m) for building world-object footprints. */
  building_default_height_m?: number;
  /** Default height (m) for dock world-object footprints. */
  dock_default_height_m?: number;
  /**
   * Optional name of a decoration biome (defined in
   * `public/decorations/index.json` under `biomes`). Defaults to
   * `"default"` when absent.
   */
  vegetation_biome?: string;
  /**
   * Optional per-terrain override of the tree-category mix. Two shapes
   * are accepted:
   *  - Flat `{ category: weight }` — applies to every surface.
   *  - Nested `{ surface: { category: weight } }` — per-surface mix.
   *    A `"default"` key (if present) acts as the fallback for surfaces
   *    not listed; otherwise the pack biome default is used.
   * Categories must match those defined in `kindToCategory` of the
   * decoration pack. Weights are normalised; missing categories count
   * as 0.
   */
  vegetation_tree_mix?:
    | Record<string, number>
    | Record<string, Record<string, number>>;
  /**
   * Optional per-terrain tree scatter density in [0, 1]. Overrides the
   * decoration layer's default tree density while keeping its spacing and
   * max-count cap.
   */
  vegetation_tree_density?: number;
  /**
   * Surface classes allowed to spawn trees. Defaults to `["forest"]`.
   * Useful for rocky coastal terrains where trees grow on non-meadow ground.
   */
  vegetation_tree_surfaces?: TerrainSurface[];
  /**
   * Broad ground material for terrain palette and ecology defaults.
   * `earthy` keeps the inland grass/brown palette, `rocky` uses pale rock
   * and scrub tones suitable for limestone/coastal terrain.
   */
  terrain_ground_type?: "earthy" | "rocky";
  /** Optional explicit override of terrain ground palette colors. */
  terrain_ground_colors?: {
    grass?: string;
    cliff?: string;
    beach?: string;
    rock?: string;
  };
  /**
   * Body of water type. Drives the default water palette tint
   * (lake = greenish/slate, sea = saturated turquoise blue).
   * Defaults to "lake" when absent.
   */
  water_type?: "lake" | "sea";
  /**
   * How exposed this basin is to the prevailing wind, in [0..1].
   * 1.0 = fully exposed (open coast / big lake), 0.0 = perfectly sheltered.
   * Used as a global multiplier on the simulated wind that drives wave
   * growth and the aerodynamic force acting on boats — a sheltered bay
   * such as Zielona Zatoka feels the same forecast wind less harshly.
   * Defaults to 1.0 when absent.
   */
  wind_exposure?: number;
  /**
   * Hard cap on the FFT wave-amplitude scale (dimensionless, ~ metres for
   * default cascades). Small enclosed waters cap out at much smaller waves
   * regardless of how high the user pushes the wind slider. Defaults to
   * 2.0 (effectively uncapped at lake scale).
   */
  max_wave_amp_scale?: number;
  /**
   * Optional explicit override of water palette colors (sRGB hex strings).
   * When present, beats the `water_type` preset.
   */
  water_colors?: {
    shallow?: string;
    mid?: string;
    deep?: string;
  };
}

export interface TerrainCompiledRasterMeta {
  file: string;
  width: number;
  height: number;
  format: "r8" | "rgba8";
}

export type TerrainFeaturePoint = [number, number];

export type TerrainFeatureGeometry =
  | { type: "point"; point: TerrainFeaturePoint }
  | { type: "line"; points: TerrainFeaturePoint[] }
  | { type: "polyline"; points: TerrainFeaturePoint[] }
  | { type: "polygon"; points: TerrainFeaturePoint[] };

export interface TerrainBuoyObject {
  id: string;
  type: "buoy";
  kind:
    | "mooring_standard"
    | "mooring_large"
    | "lateral_port"
    | "lateral_starboard"
    | "cardinal_n"
    | "cardinal_e"
    | "cardinal_s"
    | "cardinal_w";
  position: TerrainFeaturePoint;
  headingY?: number;
  anchored?: boolean;
}

export interface TerrainWindsockObject {
  id: string;
  type: "windsock";
  position: TerrainFeaturePoint;
  headingY?: number;
}

export interface TerrainEmbankmentObject {
  id: string;
  type: "embankment";
  geometry: Extract<TerrainFeatureGeometry, { type: "polyline" | "polygon" }>;
  material?: "concrete" | "stone";
}

export interface TerrainBridgeObject {
  id: string;
  type: "bridge";
  geometry: Extract<TerrainFeatureGeometry, { type: "polygon" }>;
  /** Vertical offset of deck base above water level. */
  height_m?: number;
  /** Optional clearance under the deck (informational). */
  clearance_m?: number;
  /** Optional fixed yaw override (radians, around +Y). */
  headingY?: number;
  /** Optional yaw offset in degrees added to OBB-derived heading (or used
   *  directly when the polygon is degenerate). Authored in Nova. */
  heading_deg?: number;
  /** Optional world-space X/Z offsets added to the placement (m). */
  offset_x_m?: number;
  offset_z_m?: number;
  /** "procedural" (default) or "glb" — selects the runtime renderer. */
  render_mode?: "procedural" | "glb";
  /** Bundle-relative path to the GLB model when render_mode === "glb". */
  model?: string;
  /** When false, skip pushing a runtime collider AABB (decorative only). Defaults to true. */
  collision?: boolean;
  /** When false, skip auto-generated side support colliders under the
   *  clearance band. Defaults to true — useful for genuinely floating
   *  obstacles (cable spans) that have no pillars/abutments to hit. */
  supports?: boolean;
  properties?: Record<string, unknown>;
}

/**
 * Polish inland-waterway sign mounted on a 1.5 m dark post. Emitted from a
 * Nova `TerrainItem` whose type slug is `water_sign` (procedural_key
 * `water_sign`). Per-item `properties` drive the rendered glyph:
 *  - `sign_code`: A1, A6, B8, E1, E2, E3 (case-insensitive in the runtime).
 *  - `value`: optional numeric value baked onto the plate (km/h or m).
 *  - `unit`: informational only (the glyph already implies the unit).
 *  - `rotation_deg`: yaw around +Y in degrees; 0 = plate front faces +Z.
 */
export interface TerrainWaterSignObject {
  id: string;
  type: "water_sign";
  position: TerrainFeaturePoint;
  render_mode?: "procedural";
  properties?: Record<string, unknown>;
}

/**
 * Catch-all for any other authored object rendered as a GLB
 * instance (Nova object type with render_mode === "glb" that isn't one
 * of the specialised types above, e.g. canal pipes, gates, fishing
 * platforms, custom one-offs). Geometry may be a point, line, or
 * polygon — the runtime computes the placement (cx, cz) as:
 *  - point: the projected position;
 *  - line: midpoint of the polyline (cumulative arc-length 50%);
 *  - polygon: polygon centroid.
 * `position` is always populated by the baker as a precomputed centroid
 * so the runtime never has to inspect the geometry shape.
 */
export interface TerrainGlbPolygonObject {
  id: string;
  /** Free-form type slug from Nova (procedural_key or object type slug). */
  type: string;
  /** Optional authored shape — present for line/polygon items; absent
   *  when the item is a single point. */
  geometry?: Extract<TerrainFeatureGeometry, { type: "line" | "polygon" }>;
  /** Pre-computed placement: point itself, line midpoint, or polygon
   *  centroid (depending on the authored geometry). */
  position?: TerrainFeaturePoint;
  render_mode: "glb";
  /** Bundle-relative path to the GLB model. */
  model: string;
  /** Vertical offset above water level (m). Defaults to 0. */
  height_m?: number;
  /** Optional world-space X/Z offsets added to the placement (m). */
  offset_x_m?: number;
  offset_z_m?: number;
  /** Optional fixed yaw override (radians, around +Y). */
  headingY?: number;
  /** Yaw offset in degrees authored in Nova. */
  heading_deg?: number;
  /** Optional clearance under the obstacle (m above water level). When set,
   *  the runtime collider AABB starts at `waterLevel + clearance_m` so the
   *  boat can pass underneath (bridges, pipes, gantries). */
  clearance_m?: number;
  /** When false, skip pushing a runtime collider AABB (decorative only). Defaults to true. */
  collision?: boolean;
  /** When false, skip auto-generated side support colliders under the
   *  clearance band. Defaults to true. */
  supports?: boolean;
  properties?: Record<string, unknown>;
}

/**
 * Camera flyby polyline authored in Nova as a `TerrainItem` whose type
 * slug is `flyby_line` (procedural_key `flyby_line`). It is NOT a
 * visible world object — the runtime uses it to drive a preview camera
 * along the path. Per-item `properties` drive the flight:
 *  - `height_m`: camera altitude above water level (m).
 *  - `speed_mps`: traversal speed along the path (m/s).
 *  - `loop`: when true, restart from the first point after the last;
 *    otherwise stop at the end.
 *  - `look_ahead_m`: optional distance ahead along the path used as the
 *    camera look-at target.
 * Intentionally kept OUT of the `TerrainWorldObject` union so existing
 * builders that exhaustively narrow on `type` stay unaffected; consumers
 * filter `terrain.worldObjects` on `type === "flyby_line"` themselves.
 */
export interface TerrainFlybyLineObject {
  id: string;
  type: "flyby_line";
  geometry: Extract<TerrainFeatureGeometry, { type: "line" }>;
  render_mode: "procedural";
  properties?: {
    height_m?: number;
    speed_mps?: number;
    loop?: boolean;
    look_ahead_m?: number;
    [key: string]: unknown;
  };
}

export type TerrainWorldObject =
  | TerrainBuoyObject
  | TerrainWindsockObject
  | TerrainEmbankmentObject
  | TerrainBridgeObject
  | TerrainWaterSignObject;

export interface TerrainWorldObjectsDocument {
  version: 1;
  objects: TerrainWorldObject[];
}

export type TerrainGameplayZoneType = TerrainGameplayRole;

export interface TerrainGameplayZone {
  id: string;
  type: TerrainGameplayZoneType;
  geometry: TerrainFeatureGeometry;
  headingY?: number;
}

export interface TerrainGameplayDocument {
  version: 1;
  zones: TerrainGameplayZone[];
}

export interface TerrainAsset {
  /** Stable id (folder name under public/terrain/). */
  id: string;
  /** Display label. */
  name: string;
  /** Bundle directory URL ending in '/' (e.g. "terrain/zielona_zatoka/"). */
  baseUrl: string;
  /** Source bbox + dimensions copied from terrain_meta.json. */
  meta: TerrainMetaJson;
  /** Heightmap width  in samples (= meta.output_size). */
  width: number;
  /** Heightmap height in samples (= meta.output_size). */
  height: number;
  /** World extent in metres (X axis). */
  worldWidthM: number;
  /** World extent in metres (Z axis). */
  worldHeightM: number;
  /**
   * Y-coordinate of each height sample, in metres ABOVE water level
   * (so positive = land, negative = lakebed). Row-major, +Z south:
   *   index = row * width + col,  row 0 = north edge, col 0 = west edge.
   */
  heights: Float32Array;
  /** Min/max of `heights` in metres relative to water level. */
  heightMinM: number;
  heightMaxM: number;
  /** Optional processed heightmap matching the terrain renderer layout. */
  renderHeights?: Float32Array;
  renderWidth?: number;
  renderHeight?: number;
  renderExtentM?: number;
  /** Baked L1/L2 surface classification, one byte per texel. */
  classificationSurfaceCodes?: Uint8Array;
  classificationWidth?: number;
  classificationHeight?: number;
  /** Baked marker classification, one byte per texel. */
  markerCodes?: Uint8Array;
  markerWidth?: number;
  markerHeight?: number;
  /** Baked L3 world objects. */
  worldObjects: TerrainWorldObject[];
  /** Baked L4 gameplay zones. */
  gameplayZones: TerrainGameplayZone[];
}

/** Schema of `public/terrain/index.json`. */
export interface TerrainLibraryIndex {
  terrains: Array<{
    id: string;
    name: string;
    /** Path relative to baseUrl, ending in '/'. */
    url: string;
  }>;
}

/**
 * Decode a 16-bit grayscale PNG to a Float32 heightfield in metres above
 * water level.
 *
 * Browsers do not expose 16-bit data through Canvas2D — decoding RGBA
 * channels gives 8-bit only. We use `createImageBitmap` then read back
 * via OffscreenCanvas at 8-bit; for our manoeuvring sim 8-bit precision
 * over a 16.7 m range (Zielona Zatoka) is ~6.5 cm/step which is fine.
 *
 * For higher precision we would parse the PNG IDAT chunks manually,
 * but that's overkill for the current product.
 */
async function decodeHeightmap(
  url: string,
  meta: TerrainMetaJson,
): Promise<{ width: number; height: number; heights: Float32Array }> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`TerrainAsset: failed to fetch ${url} (${res.status})`);
  }
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);
  const w = bitmap.width;
  const h = bitmap.height;
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("TerrainAsset: 2D context unavailable");
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const img = ctx.getImageData(0, 0, w, h);
  // Pillow saves 16-bit gray PNGs as 8-bit on the Canvas read path. Both
  // R and G channels carry the same MSB byte; we average them just in
  // case the browser dithers, which gives ~9 effective bits.
  const range = meta.elevation_range_m;
  const minOffset = meta.elevation_min_m - meta.water_level_m;
  const heights = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const r = img.data[i * 4]!;
    const g = img.data[i * 4 + 1]!;
    const norm = (r + g) / (2 * 255);
    heights[i] = minOffset + norm * range;
  }
  return { width: w, height: h, heights };
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`TerrainAsset: failed to fetch ${url} (${res.status})`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

async function loadCompiledSurface(
  dir: string,
  meta: TerrainMetaJson,
): Promise<{ width: number; height: number; data: Uint8Array }> {
  const surface = meta.compiled_surface;
  if (!surface) {
    throw new Error(
      "TerrainAsset: terrain_meta.json is missing compiled_surface",
    );
  }
  if (surface.format !== "r8") {
    throw new Error(
      `TerrainAsset: unsupported compiled_surface format ${surface.format}`,
    );
  }
  const data = await fetchBytes(`${dir}${surface.file}`);
  const expected = surface.width * surface.height;
  if (data.length !== expected) {
    throw new Error(
      `TerrainAsset: ${surface.file} has ${data.length} byte(s), expected ${expected}`,
    );
  }
  return { width: surface.width, height: surface.height, data };
}

async function loadCompiledMarkers(
  dir: string,
  meta: TerrainMetaJson,
): Promise<{ width: number; height: number; data: Uint8Array } | null> {
  const markers = meta.compiled_markers;
  if (!markers) return null;
  if (markers.format !== "r8") {
    throw new Error(
      `TerrainAsset: unsupported compiled_markers format ${markers.format}`,
    );
  }
  const data = await fetchBytes(`${dir}${markers.file}`);
  const expected = markers.width * markers.height;
  if (data.length !== expected) {
    throw new Error(
      `TerrainAsset: ${markers.file} has ${data.length} byte(s), expected ${expected}`,
    );
  }
  return { width: markers.width, height: markers.height, data };
}

async function loadJsonRequired<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`TerrainAsset: failed to fetch ${url} (${res.status})`);
  }
  return (await res.json()) as T;
}

/**
 * Load a single terrain folder.
 * @param baseUrl  Path to the terrain directory, ending in '/' (e.g. "terrain/zielona_zatoka/").
 * @param id       Stable identifier (defaults to last path segment).
 * @param name     Display name (defaults to id).
 */
export async function loadTerrain(
  baseUrl: string,
  id?: string,
  name?: string,
): Promise<TerrainAsset> {
  const dir = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const metaRes = await fetch(`${dir}terrain_meta.json`);
  if (!metaRes.ok) {
    throw new Error(
      `TerrainAsset: missing terrain_meta.json at ${dir} (${metaRes.status})`,
    );
  }
  const meta = (await metaRes.json()) as TerrainMetaJson;

  const { width, height, heights } = await decodeHeightmap(
    `${dir}heightmap.png`,
    meta,
  );

  let classificationSurfaceCodes: Uint8Array | undefined;
  let classificationWidth: number | undefined;
  let classificationHeight: number | undefined;
  const compiledSurface = await loadCompiledSurface(dir, meta);
  classificationSurfaceCodes = compiledSurface.data;
  classificationWidth = compiledSurface.width;
  classificationHeight = compiledSurface.height;

  let markerCodes: Uint8Array | undefined;
  let markerWidth: number | undefined;
  let markerHeight: number | undefined;
  const compiledMarkers = await loadCompiledMarkers(dir, meta);
  if (compiledMarkers) {
    markerCodes = compiledMarkers.data;
    markerWidth = compiledMarkers.width;
    markerHeight = compiledMarkers.height;
  }

  const worldObjectsDoc = await loadJsonRequired<TerrainWorldObjectsDocument>(
    `${dir}${meta.world_objects ?? "terrain_objects.json"}`,
  );
  const gameplayDoc = await loadJsonRequired<TerrainGameplayDocument>(
    `${dir}${meta.gameplay_zones ?? "terrain_gameplay.json"}`,
  );

  let heightMinM = Infinity;
  let heightMaxM = -Infinity;
  for (let i = 0; i < heights.length; i++) {
    const y = heights[i]!;
    if (y < heightMinM) heightMinM = y;
    if (y > heightMaxM) heightMaxM = y;
  }

  const finalId = id ?? dir.replace(/\/$/, "").split("/").pop() ?? "terrain";
  return {
    id: finalId,
    name: name ?? finalId,
    baseUrl: dir,
    meta,
    width,
    height,
    worldWidthM: meta.width_m,
    worldHeightM: meta.height_m,
    heights,
    heightMinM,
    heightMaxM,
    classificationSurfaceCodes,
    classificationWidth,
    classificationHeight,
    markerCodes,
    markerWidth,
    markerHeight,
    worldObjects: worldObjectsDoc.objects ?? [],
    gameplayZones: gameplayDoc.zones ?? [],
  };
}

/**
 * Sample terrain height (m above water level) at world coords (x, z).
 * Bilinear filtered. Returns 0 outside the bbox.
 *
 * World convention: terrain is centred at origin, +X east, +Z south.
 */
export function sampleTerrainHeight(
  terrain: TerrainAsset,
  x: number,
  z: number,
): number {
  if (
    terrain.renderHeights &&
    terrain.renderWidth &&
    terrain.renderHeight &&
    terrain.renderExtentM
  ) {
    return sampleHeightGrid(
      terrain.renderHeights,
      terrain.renderWidth,
      terrain.renderHeight,
      terrain.renderExtentM,
      terrain.renderExtentM,
      x,
      z,
    );
  }

  return sampleHeightGrid(
    terrain.heights,
    terrain.width,
    terrain.height,
    terrain.worldWidthM,
    terrain.worldHeightM,
    x,
    z,
  );
}

function sampleHeightGrid(
  heights: Float32Array,
  width: number,
  height: number,
  worldWidthM: number,
  worldHeightM: number,
  x: number,
  z: number,
): number {
  const halfW = worldWidthM * 0.5;
  const halfH = worldHeightM * 0.5;
  if (x < -halfW || x > halfW || z < -halfH || z > halfH) return 0;
  // Normalise to [0, 1] from west and from north.
  const u = (x + halfW) / worldWidthM;
  const v = (z + halfH) / worldHeightM;
  const fx = u * (width - 1);
  const fy = v * (height - 1);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const a = heights[y0 * width + x0]!;
  const b = heights[y0 * width + x1]!;
  const c = heights[y1 * width + x0]!;
  const d = heights[y1 * width + x1]!;
  const ab = a + (b - a) * tx;
  const cd = c + (d - c) * tx;
  return ab + (cd - ab) * ty;
}

export type TerrainSurface =
  | "water"
  | "forest"
  | "meadow"
  | "road"
  | "bridge"
  | "building"
  | "dock"
  | "parking"
  | "embankment"
  | "railway"
  | "powerline"
  | "wetland"
  | "outside"
  | "major_road"
  | "other";

/** Numeric encoding used when packing classification into a `r8uint` texture.
 *  Must be kept in sync with the enum in `shaders/terrain.wgsl`. */
export const SURFACE_CODE: Record<TerrainSurface, number> = {
  other: 0,
  water: 1,
  forest: 2,
  meadow: 3,
  road: 4,
  bridge: 5,
  building: 6,
  dock: 7,
  parking: 8,
  embankment: 9,
  railway: 10,
  powerline: 11,
  wetland: 12,
  outside: 13,
  major_road: 14,
};

export type AnnotationMarker =
  | "windsock"
  | "powerline"
  | "bridge"
  | "dock"
  | "buoy_mooring_standard"
  | "buoy_mooring_large"
  | "buoy_lateral_port"
  | "buoy_lateral_starboard"
  | "buoy_cardinal_n"
  | "buoy_cardinal_e"
  | "buoy_cardinal_s"
  | "buoy_cardinal_w";

export const MARKER_CODE: Record<AnnotationMarker, number> = {
  windsock: 1,
  powerline: 2,
  buoy_mooring_standard: 3,
  buoy_mooring_large: 4,
  buoy_lateral_port: 5,
  buoy_lateral_starboard: 6,
  buoy_cardinal_n: 7,
  buoy_cardinal_e: 8,
  buoy_cardinal_s: 9,
  buoy_cardinal_w: 10,
  bridge: 11,
  dock: 12,
};

const MARKER_BY_SURFACE: Partial<Record<TerrainSurface, AnnotationMarker>> = {
  bridge: "bridge",
  dock: "dock",
};

const SURFACE_BY_CODE: Record<number, TerrainSurface> = Object.fromEntries(
  Object.entries(SURFACE_CODE).map(([surface, code]) => [code, surface]),
) as Record<number, TerrainSurface>;

export interface TerrainSemanticMask {
  width: number;
  height: number;
  mask: Uint8Array;
}

export function terrainClassificationDimensions(
  terrain: TerrainAsset,
): { width: number; height: number } | null {
  if (
    terrain.classificationWidth &&
    terrain.classificationHeight &&
    terrain.classificationSurfaceCodes
  ) {
    return {
      width: terrain.classificationWidth,
      height: terrain.classificationHeight,
    };
  }
  return null;
}

export function buildTerrainSurfaceMask(
  terrain: TerrainAsset,
  surface: TerrainSurface,
): TerrainSemanticMask | null {
  if (
    terrain.classificationSurfaceCodes &&
    terrain.classificationWidth &&
    terrain.classificationHeight
  ) {
    const want = SURFACE_CODE[surface];
    const mask = new Uint8Array(terrain.classificationSurfaceCodes.length);
    for (let i = 0; i < terrain.classificationSurfaceCodes.length; i++) {
      if (terrain.classificationSurfaceCodes[i] === want) mask[i] = 1;
    }
    const marker = MARKER_BY_SURFACE[surface];
    if (
      marker &&
      terrain.markerCodes &&
      terrain.markerWidth === terrain.classificationWidth &&
      terrain.markerHeight === terrain.classificationHeight
    ) {
      const markerWant = MARKER_CODE[marker];
      for (let i = 0; i < terrain.markerCodes.length; i++) {
        if (terrain.markerCodes[i] === markerWant) mask[i] = 1;
      }
    }
    return {
      width: terrain.classificationWidth,
      height: terrain.classificationHeight,
      mask,
    };
  }
  return null;
}

export function buildTerrainMarkerMask(
  terrain: TerrainAsset,
  marker: AnnotationMarker,
): TerrainSemanticMask | null {
  if (terrain.markerCodes && terrain.markerWidth && terrain.markerHeight) {
    const want = MARKER_CODE[marker];
    const mask = new Uint8Array(terrain.markerCodes.length);
    for (let i = 0; i < terrain.markerCodes.length; i++) {
      if (terrain.markerCodes[i] === want) mask[i] = 1;
    }
    return {
      width: terrain.markerWidth,
      height: terrain.markerHeight,
      mask,
    };
  }
  return null;
}

function sampleClassificationCode(
  terrain: TerrainAsset,
  x: number,
  z: number,
  data: Uint8Array | undefined,
  width: number | undefined,
  height: number | undefined,
): number {
  if (!data || !width || !height) {
    return 0;
  }
  const halfW = terrain.worldWidthM * 0.5;
  const halfH = terrain.worldHeightM * 0.5;
  if (x < -halfW || x > halfW || z < -halfH || z > halfH) return 0;
  const u = (x + halfW) / terrain.worldWidthM;
  const v = (z + halfH) / terrain.worldHeightM;
  const px = Math.min(width - 1, Math.floor(u * width));
  const py = Math.min(height - 1, Math.floor(v * height));
  return data[py * width + px] ?? 0;
}

export function sampleTerrainMarker(
  terrain: TerrainAsset,
  x: number,
  z: number,
): AnnotationMarker | null {
  const markerCode = sampleClassificationCode(
    terrain,
    x,
    z,
    terrain.markerCodes,
    terrain.markerWidth,
    terrain.markerHeight,
  );
  for (const [marker, code] of Object.entries(MARKER_CODE)) {
    if (code === markerCode) return marker as AnnotationMarker;
  }
  return null;
}

/**
 * Sample surface classification at world coords. Nearest-neighbour to keep
 * crisp boundaries. Runtime reads the baked L1/L2 compiled surface raster.
 */
export function sampleTerrainSurface(
  terrain: TerrainAsset,
  x: number,
  z: number,
): TerrainSurface {
  const halfW = terrain.worldWidthM * 0.5;
  const halfH = terrain.worldHeightM * 0.5;
  if (x < -halfW || x > halfW || z < -halfH || z > halfH) return "water";
  const surfaceCode = sampleClassificationCode(
    terrain,
    x,
    z,
    terrain.classificationSurfaceCodes,
    terrain.classificationWidth,
    terrain.classificationHeight,
  );
  const classifiedSurface = SURFACE_BY_CODE[surfaceCode];
  if (classifiedSurface) return classifiedSurface;
  return "other";
}

/**
 * Spiral search for the nearest water cell starting at (x, z). Returns
 * world-space (x, z) of the cell centre, or null if the bbox contains no
 * water. Step defaults to ~5 m which is comfortable for classification
 * tiles and keeps the search fast on huge bboxes.
 */
export function findNearestWaterCell(
  terrain: TerrainAsset,
  x: number,
  z: number,
  stepM = 5,
  maxRadiusM?: number,
): [number, number] | null {
  if (sampleTerrainSurface(terrain, x, z) === "water") return [x, z];
  const rMax =
    maxRadiusM ?? Math.max(terrain.worldWidthM, terrain.worldHeightM) * 0.5;
  // Walk concentric square rings.
  for (let r = stepM; r <= rMax; r += stepM) {
    const steps = Math.max(8, Math.ceil((2 * Math.PI * r) / stepM));
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      const px = x + Math.cos(a) * r;
      const pz = z + Math.sin(a) * r;
      if (sampleTerrainSurface(terrain, px, pz) === "water") {
        return [px, pz];
      }
    }
  }
  return null;
}

/**
 * Load every terrain referenced by `<baseUrl>index.json`. Failures for
 * individual terrains are logged but do not abort the whole load.
 */
export async function loadTerrainLibrary(
  baseUrl = "terrain/",
): Promise<Map<string, TerrainAsset>> {
  const indexUrl = `${baseUrl}index.json`;
  const res = await fetch(indexUrl);
  if (!res.ok) {
    throw new Error(`TerrainAsset: missing ${indexUrl} (${res.status})`);
  }
  const idx = (await res.json()) as TerrainLibraryIndex;
  const out = new Map<string, TerrainAsset>();
  await Promise.all(
    idx.terrains.map(async (entry) => {
      try {
        const url = entry.url.startsWith(baseUrl)
          ? entry.url
          : `${baseUrl}${entry.url.replace(/^terrain\//, "")}`;
        const t = await loadTerrain(url, entry.id, entry.name);
        out.set(entry.id, t);
      } catch (err) {
        console.error(`TerrainAsset: failed to load "${entry.id}"`, err);
      }
    }),
  );
  return out;
}
